import React, { useState } from 'react';
import { 
  Users, 
  ShieldCheck, 
  AlertTriangle, 
  PlusCircle, 
  Trash2, 
  CheckCircle2, 
  Lock, 
  Mail, 
  Phone, 
  ArrowRight,
  Info
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { FamilyMember } from '../types';

export const FamilyProtectionPage: React.FC = () => {
  const { familyMembers, addFamily, removeFamily, currentPlanId } = useStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState<FamilyMember['relationship']>('Partner');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !consentChecked) return;

    setIsSubmitting(true);
    try {
      await addFamily({
        name: name.trim(),
        relationship,
        email: email.trim(),
        phone: phone.trim() || undefined
      });
      setName('');
      setEmail('');
      setPhone('');
      setConsentChecked(false);
      setShowAddModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFamilyPlan = currentPlanId === 'family';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950 text-purple-300 text-xs font-semibold border border-purple-800">
            <Users className="w-3.5 h-3.5" />
            <span>Family Identity Protection Hub</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mt-2">
            Family Security Circle
          </h1>
          <p className="text-slate-400 text-xs mt-1">
            Monitor and protect up to 5 family members (partner, children, elderly parents) from scams and data breaches.
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold shadow-lg shadow-purple-950/40 transition-colors flex items-center gap-2 self-start sm:self-auto"
        >
          <PlusCircle className="w-4 h-4" />
          <span>Add Family Member</span>
        </button>
      </div>

      {/* Plan Status Banner */}
      {!isFamilyPlan && (
        <div className="p-5 rounded-2xl bg-gradient-to-r from-purple-950/80 via-slate-900 to-slate-900 border border-purple-800/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="space-y-1 text-xs">
            <span className="font-bold text-purple-300 uppercase tracking-wider text-[10px]">Family Protection Preview</span>
            <p className="text-slate-200">
              You are currently on Personal plan. Upgrade to Family Plan ($19.95/mo) to unlock continuous monitoring for up to 5 family members.
            </p>
          </div>
          <a
            href="/pricing"
            className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs whitespace-nowrap transition-colors"
          >
            Upgrade to Family Plan
          </a>
        </div>
      )}

      {/* Family Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {familyMembers.map((member) => (
          <div
            key={member.id}
            className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4 shadow-xl flex flex-col justify-between"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl ${member.avatarColor} text-white font-bold flex items-center justify-center text-sm shadow-md`}>
                    {member.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-white">{member.name}</h3>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-800 text-slate-300 border border-slate-700">
                        {member.relationship}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 font-mono">{member.email}</p>
                  </div>
                </div>

                {member.relationship !== 'Me' && (
                  <button
                    onClick={() => removeFamily(member.id)}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 transition-colors"
                    title="Remove family member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Status and Score Row */}
              <div className="grid grid-cols-2 gap-2 pt-2 text-xs">
                <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] text-slate-500 font-medium">Security Score</span>
                  <div className="text-sm font-bold text-emerald-400">{member.securityScore} / 100</div>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] text-slate-500 font-medium">Active Alerts</span>
                  <div className={`text-sm font-bold ${member.activeAlertsCount > 0 ? 'text-amber-400' : 'text-slate-300'}`}>
                    {member.activeAlertsCount > 0 ? `${member.activeAlertsCount} Pending` : '0 Alerts'}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-xs">
              <span className="text-slate-400 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span>Monitoring Active</span>
              </span>
              <span className="text-[11px] text-purple-400 font-medium font-mono">
                Consent Verified
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <h3 className="text-lg font-bold text-white">Add Family Member</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleAddMember} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="block font-semibold text-slate-300">Relationship:</label>
                <select
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-purple-500"
                >
                  <option value="Partner">Partner / Spouse</option>
                  <option value="Child">Child / Teenager</option>
                  <option value="Parent">Parent / Elderly Relative</option>
                  <option value="Dependent">Dependent</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block font-semibold text-slate-300">Full Name:</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Emma Mitchell"
                  required
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-purple-500"
                />
              </div>

              <div className="space-y-1">
                <label className="block font-semibold text-slate-300">Email Address (for breach surveillance):</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. emma@example.com.au"
                  required
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-purple-500"
                />
              </div>

              <div className="space-y-1">
                <label className="block font-semibold text-slate-300">Mobile Phone (Optional AU number):</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. +61 400 123 456"
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Consent Control (Section 17) */}
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="family_consent"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="rounded mt-0.5 accent-purple-600"
                  required
                />
                <label htmlFor="family_consent" className="text-[11px] text-slate-300 leading-snug">
                  I confirm that I have obtained consent from this adult family member (or have legal guardianship for a dependent child) to monitor breach exposures under Australian Privacy legislation.
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl text-slate-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !consentChecked}
                  className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold transition-colors"
                >
                  {isSubmitting ? 'Adding...' : 'Add to Family Shield'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
